import { useEffect, useRef } from "react";
import { useMCPTT } from "../mcptt/hooks";

/**
 * 4-segment vertical LED-style level meter.
 *
 * Reads RX or TX RMS from the WebAudioAdapter at ~30Hz via
 * requestAnimationFrame (we throttle to every other frame to keep
 * cost negligible). Light segments fill bottom-up; the top segment
 * lights only on near-clipping.
 *
 * Mode:
 *   - "rx": reads getRxLevel(); colour green->amber->red.
 *   - "tx": reads getTxLevel(); colour pure red (matches the TX
 *           visual language).
 *
 * The meter mounts as long as audioState !== "off". When state is
 * "off" we render nothing — there's no audio context to poll.
 */
export function AudioMeter({ mode }: { mode: "rx" | "tx" }) {
  const { snapshot, getRxLevel, getTxLevel } = useMCPTT();
  const barsRef = useRef<HTMLSpanElement[]>([]);
  const rafRef  = useRef<number | null>(null);
  const tickRef = useRef(0);

  useEffect(() => {
    if (snapshot.audioState === "off" || snapshot.audioState === "error") return;

    const read = mode === "rx" ? getRxLevel : getTxLevel;
    const segCount = barsRef.current.length;

    function loop() {
      rafRef.current = requestAnimationFrame(loop);
      // Throttle to every other rAF to land near 30 Hz on a 60Hz display.
      tickRef.current++;
      if ((tickRef.current & 1) !== 0) return;

      const lvl = read();
      // Map [0..1] to [0..segCount] segments, with a small noise floor
      // to avoid a constantly-twitching first segment.
      const lit = lvl < 0.02 ? 0 : Math.min(segCount, Math.ceil(lvl * segCount));
      for (let i = 0; i < segCount; i++) {
        const on = i < lit;
        const el = barsRef.current[i];
        if (!el) continue;
        if (on !== el.classList.contains("on")) {
          el.classList.toggle("on", on);
        }
      }
    }
    rafRef.current = requestAnimationFrame(loop);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };
  }, [snapshot.audioState, mode, getRxLevel, getTxLevel]);

  if (snapshot.audioState === "off" || snapshot.audioState === "error") return null;

  return (
    <span
      className={`audio-meter audio-meter--${mode}`}
      title={mode === "tx" ? "Microphone level" : "Receive level"}
      aria-hidden="true"
    >
      {[0, 1, 2, 3].map((i) => (
        <span
          key={i}
          className="audio-meter-seg"
          ref={(el) => { if (el) barsRef.current[i] = el; }}
        />
      ))}
    </span>
  );
}
